import { fmtN, fmtP, PaceArrow } from "./Momentum";
import { TH, StageCells, GroupHeads, SubHeads, totalCell, pctOf, colLabels } from "./StageCells";

const STAGES = [["enquiry", "Enquiry"], ["test_drive", "Test Drive"], ["booking", "Booking"], ["retail", "Retail"]];
const safe = (s) => s.replace(/[^A-Za-z0-9]+/g, "_");

function Head({ period, first, tail }) {
  return (
    <thead className="border-b sticky top-0 bg-card z-[2]">
      <tr>
        <th className={`${TH} text-left sticky left-0 bg-card z-[3]`}>{first}</th>
        <GroupHeads stages={STAGES} freezeRetail testPrefix={`ov-${first.toLowerCase()}`} />
        {tail.map((t, i) => <th key={t} className={`${TH} ${i === 0 ? "border-l-2 border-border/80" : ""} ${t === "7-day" || t === "DoD" ? "text-center" : "text-right"}`}>{t}</th>)}
      </tr>
      <tr className="text-[9px] text-muted-foreground">
        <th className="sticky left-0 bg-card" />
        <SubHeads stages={STAGES} period={period} freezeRetail />
        {tail.map((t) => <th key={t} />)}
      </tr>
    </thead>
  );
}

export function ModelTable({ rows, active, onPick, period }) {
  const future = period?.future;
  const total = Object.fromEntries(STAGES.map(([k]) => [k, totalCell(rows, (r) => r.cells[k], future)]));
  const [planLbl, tdLbl] = colLabels(period);
  const pace = period?.kind === "week" ? "DoD" : "7-day";
  return (
    <div className="border bg-card rounded-md" data-testid="ov-model-table">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-2"><h3 className="font-heading text-sm font-semibold">D · Product (model) <span className="text-muted-foreground font-normal text-xs">· {planLbl} / {tdLbl} / Ach / % · click a model to filter the page</span></h3>{active && <button onClick={() => onPick("")} className="text-[11px] underline" data-testid="ov-model-clear">clear {active}</button>}</div>
      <div className="overflow-auto max-h-[480px]">
        <table className="w-full text-xs min-w-[1240px]">
          <Head period={period} first="Model" tail={["Mix plan / ach", pace, "Order bank"]} />
          <tbody>
            {rows.map((r) => (
              <tr key={r.model} onClick={() => onPick(active === r.model ? "" : r.model)} className={`border-b last:border-0 cursor-pointer hover:bg-muted/50 ${active === r.model ? "bg-amber-50" : "bg-card"}`} data-testid={`ov-model-${safe(r.model)}`}>
                <td className="px-2 py-1 font-medium whitespace-nowrap sticky left-0 bg-inherit">{r.model}</td>
                {STAGES.map(([k]) => <StageCells key={k} c={r.cells[k]} period={period} retail={k === "retail"} sticky={k === "retail"} bg={active === r.model ? "bg-amber-50" : "bg-card"} testPrefix={`ov-model-${safe(r.model)}-${k}`} />)}
                <td className="px-2 py-1 text-right num text-muted-foreground border-l-2 border-border/80 whitespace-nowrap">{fmtP(r.mix_plan)} / <span className="text-foreground">{fmtP(r.mix_ach)}</span></td>
                <td className="px-2 py-1 text-center"><PaceArrow m={r.momentum} compact /></td>
                <td className="px-2 py-1 text-right num">{fmtN(r.order_bank)}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="bg-secondary font-bold border-t-2 border-foreground/30" data-testid="ov-model-total">
                <td className="px-2 py-1.5 uppercase tracking-wider text-[10px] sticky left-0 bg-secondary">Total</td>
                {STAGES.map(([k]) => <StageCells key={k} c={total[k]} period={period} retail={k === "retail"} sticky={k === "retail"} bg="bg-secondary" testPrefix={`ov-model-total-${k}`} />)}
                <td className="px-2 py-1.5 text-right num text-muted-foreground border-l-2 border-border/80">100% / 100%</td><td /><td className="px-2 py-1.5 text-right num">{fmtN(rows.reduce((s, r) => s + (r.order_bank || 0), 0))}</td>
              </tr>
            )}
            {rows.length === 0 && <tr><td colSpan={20} className="px-3 py-6 text-center text-muted-foreground">No model data</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SourceTable({ rows, active, onPick, period }) {
  const future = period?.future;
  const total = Object.fromEntries(STAGES.map(([k]) => [k, totalCell(rows, (r) => r.cells[k], future)]));
  const [planLbl, tdLbl] = colLabels(period);
  const pace = period?.kind === "week" ? "DoD" : "7-day";
  return (
    <div className="border bg-card rounded-md" data-testid="ov-source-table">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-2"><h3 className="font-heading text-sm font-semibold">E · Lead source <span className="text-muted-foreground font-normal text-xs">· {planLbl} / {tdLbl} / Ach / % · click a source to filter the page</span></h3>{active && <button onClick={() => onPick("")} className="text-[11px] underline" data-testid="ov-source-clear">clear {active}</button>}</div>
      <div className="overflow-auto max-h-[480px]">
        <table className="w-full text-xs min-w-[1280px]">
          <Head period={period} first="Source" tail={["Mix plan / ach", "Enq→Retail", pace]} />
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} onClick={() => r.source !== "UNALLOCATED" && onPick(active === r.source ? "" : r.source)} className={`border-b last:border-0 hover:bg-muted/50 ${r.source === "UNALLOCATED" ? "text-muted-foreground" : "cursor-pointer"} ${active === r.source ? "bg-amber-50" : "bg-card"}`} data-testid={`ov-source-${safe(r.source)}`}>
                <td className="px-2 py-1 font-medium whitespace-nowrap sticky left-0 bg-inherit">{r.source === "UNALLOCATED" ? "Unallocated" : r.source}</td>
                {STAGES.map(([k]) => <StageCells key={k} c={r.cells[k]} period={period} retail={k === "retail"} sticky={k === "retail"} bg={active === r.source ? "bg-amber-50" : "bg-card"} testPrefix={`ov-source-${safe(r.source)}-${k}`} />)}
                <td className="px-2 py-1 text-right num text-muted-foreground border-l-2 border-border/80 whitespace-nowrap">{fmtP(r.mix_plan)} / <span className="text-foreground">{fmtP(r.mix_ach)}</span></td>
                <td className="px-2 py-1 text-right num">{fmtP(r.conv_enq_retail)}</td>
                <td className="px-2 py-1 text-center"><PaceArrow m={r.momentum} compact /></td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="bg-secondary font-bold border-t-2 border-foreground/30" data-testid="ov-source-total">
                <td className="px-2 py-1.5 uppercase tracking-wider text-[10px] sticky left-0 bg-secondary">Total</td>
                {STAGES.map(([k]) => <StageCells key={k} c={total[k]} period={period} retail={k === "retail"} sticky={k === "retail"} bg="bg-secondary" testPrefix={`ov-source-total-${k}`} />)}
                <td className="px-2 py-1.5 text-right num text-muted-foreground border-l-2 border-border/80">100% / 100%</td><td className="px-2 py-1.5 text-right num">{fmtP(pctOf(total.retail.ach, total.enquiry.ach))}</td><td />
              </tr>
            )}
            {rows.length === 0 && <tr><td colSpan={20} className="px-3 py-6 text-center text-muted-foreground">No source data</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
